/**
 * MALIKI scroll choreography — site-wide, works in every modern browser.
 *
 * Marks content that starts below the fold and reveals it as it enters:
 *  - [data-mk="rise"]   sections/blocks fade up
 *  - [data-mk="mask"]   headlines rise out of a mask
 *  - [data-mk="unveil"] images wipe open from the bottom and settle
 *  - grid/list items are staggered with --mk-i
 * Plus scroll-linked motion:
 *  - [data-mk-drift]    giant type slides sideways with the scroll (value = speed, e.g. "-0.35")
 *  - [data-mk-float]    images drift slightly against the scroll
 * Nothing above the fold is hidden, and prefers-reduced-motion skips everything.
 */
const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function setup() {
  const main = document.querySelector("main");
  // Phones and tablets skip the scroll choreography (scroll-linked work is the main source of jank there); the desktop keeps it.
  if (!main || reduced || window.matchMedia("(max-width: 1023.98px)").matches) return;
  document.documentElement.classList.add("mk-js");

  const fold = window.innerHeight * 0.92;
  const below = (el) => el.getBoundingClientRect().top > fold;
  const skip = (el) => el.closest(".maliki-hero, [data-mk-skip], dialog, .maliki-marquee, [data-carousel-track]") !== null && !el.matches("[data-carousel-track] *:is(img)");
  const marked = new Set();
  const mark = (el, kind) => {
    if (marked.has(el) || el.dataset.mk || !below(el) || skip(el) || el.closest(".maliki-reveal")) return;
    el.dataset.mk = kind;
    marked.add(el);
  };

  main.querySelectorAll("h2, h1:not(.sr-only)").forEach((el) => !el.classList.contains("sr-only") && mark(el, "mask"));
  main.querySelectorAll("img").forEach((img) => {
    const frame = img.parentElement;
    if (frame && frame.offsetWidth > 120) mark(img, "unveil");
  });
  main.querySelectorAll("ul.grid, ol.grid, div.grid").forEach((grid) => {
    if (grid.closest("[data-carousel-track]")) return;
    [...grid.children].forEach((child, index) => {
      if (marked.has(child) || !below(child) || skip(child) || child.closest(".maliki-reveal")) return;
      child.dataset.mk = "rise";
      child.style.setProperty("--mk-i", String(index % 4));
      marked.add(child);
    });
  });
  main.querySelectorAll("p, .richtext, a.button-primary, a.button-outline, [data-carousel-progress]").forEach((el) => {
    if (el.closest("[data-mk]")) return;
    mark(el, "rise");
  });

  // Masked/unveiled elements are fully clipped before they reveal, and a fully clipped target never
  // reports as intersecting, so those are watched through their parent box instead.
  const watched = new Map();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      (watched.get(entry.target) || []).forEach((el) => el.classList.add("mk-in"));
      watched.delete(entry.target);
      observer.unobserve(entry.target);
    });
  }, { rootMargin: "0px 0px -6% 0px", threshold: 0 });
  marked.forEach((el) => {
    const clipped = el.dataset.mk === "mask" || el.dataset.mk === "unveil";
    const box = clipped && el.parentElement ? el.parentElement : el;
    if (!watched.has(box)) watched.set(box, []);
    watched.get(box).push(el);
    observer.observe(box);
  });
  // Safety net: anything still hidden after the page settles near it is shown.
  window.addEventListener("load", () => setTimeout(() => {
    marked.forEach((el) => { if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add("mk-in"); });
  }, 1200), { once: true });

  // Scroll-linked drift and float, batched in one rAF.
  const drifters = [...document.querySelectorAll("[data-mk-drift]")];
  const floaters = [...main.querySelectorAll("[data-mk-float] img")];
  if (!drifters.length && !floaters.length) return;
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    drifters.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      const speed = parseFloat(el.dataset.mkDrift) || -0.3;
      el.style.transform = `translate3d(${progress * speed * 100}%, 0, 0)`;
    });
    floaters.forEach((img) => {
      const rect = img.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      img.style.transform = `translate3d(0, ${progress * -6}%, 0) scale(1.12)`;
    });
  };
  const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  window.addEventListener("scroll", request, { passive: true });
  window.addEventListener("resize", request);
  update();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", setup);
else setup();

/* Ambient videos: load near the viewport, play while visible, pause off screen; user can pause.
   Reduced motion keeps the poster only. */
function setupVideos() {
  const frames = [...document.querySelectorAll("[data-mk-video-frame]")];
  if (!frames.length) return;
  const pickSrc = (video) => {
    const mobile = window.matchMedia("(max-width: 767px)").matches;
    return (mobile && video.dataset.srcMobile) || video.dataset.src;
  };
  if (reduced) {
    // No motion: frames without a poster (the video-only hero) show their first frame, still.
    frames.forEach((frame) => {
      const video = frame.querySelector("[data-mk-video]");
      if (video && !video.getAttribute("poster") && !video.src) {
        video.preload = "metadata";
        video.src = pickSrc(video);
      }
    });
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const frame = entry.target;
      const video = frame.querySelector("[data-mk-video]");
      if (!video) return;
      if (entry.isIntersecting) {
        if (!video.src) {
          // Phones get the lighter rendition; data-saver visitors keep the poster, except where there is none.
          if (navigator.connection && navigator.connection.saveData && video.getAttribute("poster")) return;
          video.src = pickSrc(video);
          video.load();
        }
        if (frame.dataset.userPaused !== "true") video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, { rootMargin: "200px 0px", threshold: 0.01 });
  frames.forEach((frame) => {
    const video = frame.querySelector("[data-mk-video]");
    const toggle = frame.querySelector("[data-mk-video-toggle]");
    if (toggle && video) {
      toggle.hidden = false;
      toggle.addEventListener("click", () => {
        const pause = !video.paused;
        if (pause) video.pause(); else video.play().catch(() => {});
        frame.dataset.userPaused = pause ? "true" : "false";
        toggle.setAttribute("aria-pressed", pause ? "true" : "false");
        toggle.setAttribute("aria-label", pause ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      });
    }
    observer.observe(frame);
  });
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", setupVideos);
else setupVideos();

/* Header: solid after the first scroll (transparent overlay on the homepage), hidden while
   scrolling down past the fold, back on any scroll up. Reduced motion keeps it always visible. */
function setupHeader() {
  const header = document.querySelector("[data-maliki-header]");
  if (!header) return;
  const measure = () => document.documentElement.style.setProperty("--mk-header-h", header.offsetHeight + "px");
  measure();
  window.addEventListener("resize", measure, { passive: true });
  let lastY = window.scrollY;
  let ticking = false;
  const update = () => {
    ticking = false;
    const y = window.scrollY;
    header.toggleAttribute("data-scrolled", y > 24);
    if (!reduced) {
      const goingDown = y > lastY + 4;
      const goingUp = y < lastY - 4;
      if (goingDown && y > window.innerHeight * 0.6) header.setAttribute("data-hidden", "");
      else if (goingUp || y < 120) header.removeAttribute("data-hidden");
    }
    if (Math.abs(y - lastY) > 4) lastY = y;
  };
  window.addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  update();
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", setupHeader);
else setupHeader();

/* Sillage background: pause its animations while it is off screen so the rest of the page scrolls on a quiet GPU. */
function setupSillagePause() {
  const sillages = document.querySelectorAll(".cs-sillage");
  if (!sillages.length || !("IntersectionObserver" in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.classList.toggle("cs-paused", !entry.isIntersecting));
  }, { rootMargin: "80px 0px" });
  sillages.forEach((el) => observer.observe(el));
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", setupSillagePause);
else setupSillagePause();
